import { useEffect, useRef } from 'react'
import type { PendingIntakeEntry } from './entry-intake-state'

type SyncResult = boolean | { success: boolean }

type Options = {
  pendingEntries: PendingIntakeEntry[]
  isOnline: boolean
  enabled: boolean
  sync: () => Promise<SyncResult>
  baseDelay?: number
  maxDelay?: number
}

const DEFAULT_BASE_DELAY = 5_000
const DEFAULT_MAX_DELAY = 60_000

export function usePendingEntryAutoSync({
  pendingEntries,
  isOnline,
  enabled,
  sync,
  baseDelay = DEFAULT_BASE_DELAY,
  maxDelay = DEFAULT_MAX_DELAY,
}: Options) {
  const attemptRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  const inFlightRef = useRef(false)

  useEffect(() => {
    const pendingCount = pendingEntries.length
    if (!enabled) {
      attemptRef.current = 0
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (!isOnline) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (pendingCount === 0) {
      attemptRef.current = 0
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    if (inFlightRef.current) return

    const delay = Math.min(baseDelay * Math.pow(2, attemptRef.current), maxDelay)

    timerRef.current = window.setTimeout(async () => {
      inFlightRef.current = true
      try {
        const result = await sync()
        const success = typeof result === 'boolean' ? result : Boolean(result?.success)
        attemptRef.current = success ? 0 : attemptRef.current + 1
      } catch {
        attemptRef.current += 1
      } finally {
        inFlightRef.current = false
      }
    }, delay)

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [pendingEntries, enabled, isOnline, sync, baseDelay, maxDelay])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])
}
